import { useCallback, useEffect, useRef, useState } from 'react'
import { getProviderTestimonials } from 'dataSource/ProviderSearch'

const DEFAULT_LIMIT = 10

function mapApiItemToDisplayItem(
  apiItem: ProviderTestimonialApiItem
): ProviderTestimonialItem {
  return {
    id: apiItem.id,
    reviewBody: apiItem.reviewBody,
    rating: apiItem.rating ?? null,
    datePublished: apiItem.datePublished,
    rankScore: apiItem.rankScore,
    createdAt: apiItem.createdAt ?? '',
    isFeatured: apiItem.isFeatured,
    serviceFilter: apiItem.serviceFilter?.value ?? null,
    specialtyFilter: apiItem.specialtyFilter?.value ?? null,
    authorFirstName: apiItem.authorFirstName,
  }
}

/** Next row offset (0-based). When initialItems are present, first loadMore fetches from this offset. */
function getInitialNextOffset(initialLength: number): number {
  return initialLength > 0 ? initialLength : 0
}

type PaginationState = {
  nextOffset: number
  totalCount: number | null
  totalPages: number | null
}

function getInitialPagination(initialLength: number): PaginationState {
  return {
    nextOffset: getInitialNextOffset(initialLength),
    totalCount: null,
    totalPages: null,
  }
}

function computeHasMore({
  totalCount,
  totalPages,
  nextOffset,
}: PaginationState): boolean {
  if (totalCount === null || totalPages === null) {
    return true
  }

  if (totalPages === 0 || totalCount === 0) {
    return false
  }

  return nextOffset < totalCount
}

export function usePaginatedTestimonials({
  providerId,
  initialItems = [],
  limit = DEFAULT_LIMIT,
}: UsePaginatedTestimonialsParams): UsePaginatedTestimonialsReturn {
  const [items, setItems] = useState<ProviderTestimonialItem[]>(initialItems)
  const [pagination, setPagination] = useState<PaginationState>(() =>
    getInitialPagination(initialItems.length)
  )
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const requestIdRef = useRef(0)

  useEffect(() => {
    requestIdRef.current += 1
    setItems(initialItems)
    setPagination(getInitialPagination(initialItems.length))
    setIsLoading(false)
    setError(null)
  }, [providerId, initialItems])

  const hasMore = computeHasMore(pagination)

  const loadMore = useCallback(async () => {
    if (!providerId || isLoading || !computeHasMore(pagination)) {
      return
    }

    requestIdRef.current += 1
    const requestId = requestIdRef.current

    setIsLoading(true)
    setError(null)

    try {
      const offset = pagination.nextOffset
      const response = await getProviderTestimonials(providerId, {
        offset,
        limit,
      })

      if (requestId !== requestIdRef.current) {
        return
      }

      const newItems = (response.result ?? []).map(mapApiItemToDisplayItem)
      setItems((prev) => {
        const existingIds = new Set(prev.map((item) => item.id))
        const unique = newItems.filter((item) => !existingIds.has(item.id))
        return [...prev, ...unique]
      })

      const apiPagination = response.pagination
      const count = apiPagination?.count ?? null
      const totalPagesFromApi =
        apiPagination?.totalPages ??
        (apiPagination && count != null
          ? Math.ceil(count / (apiPagination.limit ?? limit))
          : null)

      setPagination((prev) => ({
        nextOffset: prev.nextOffset + newItems.length,
        totalCount: count,
        totalPages: totalPagesFromApi,
      }))
    } catch (err) {
      if (requestId === requestIdRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)))
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false)
      }
    }
  }, [providerId, pagination, limit, isLoading])

  return {
    items,
    loadMore,
    hasMore,
    isLoading,
    error,
  }
}
